import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../store';
import { getCakeEditLogsByOrderId, clearLogs } from '../../../store/cakeEditLogs/cakeEditLogsSlice';
import { CakeEditLog } from '../../../store/cakeEditLogs/api';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VisibilityIcon from '@mui/icons-material/Visibility';

interface CakeEditLogsProps {
  orderId: string;
}

const CakeEditLogs: React.FC<CakeEditLogsProps> = ({ orderId }) => {
  const dispatch = useAppDispatch();
  const { logs, isLoading, isError, error } = useAppSelector((state) => state.cakeEditLogs);
  const [selectedLog, setSelectedLog] = useState<CakeEditLog | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (orderId) {
      dispatch(getCakeEditLogsByOrderId(orderId));
    }
    
    return () => {
      dispatch(clearLogs());
    };
  }, [dispatch, orderId]);

  const handleViewDetails = (log: CakeEditLog) => {
    setSelectedLog(log);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedLog(null);
  };

  const formatPrice = (price: number | string | null | undefined) => {
    if (price === null || price === undefined) return 'QAR 0.00';
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numericPrice)) return 'QAR 0.00';
    return `QAR ${numericPrice.toFixed(2)}`;
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFieldDisplayName = (field: string) => {
    switch (field) {
      case 'shapeId':
        return 'Shape';
      case 'flavorId':
        return 'Flavor';
      case 'toppingId':
        return 'Topping';
      case 'colorCode':
        return 'Color';
      case 'writeOnCake':
        return 'Write on Cake';
      case 'instruction':
        return 'Instruction';
      case 'printImageUrl':
        return 'Print Image';
      case 'displayMode':
        return 'Display Mode';
      default:
        return field.replace(/([A-Z])/g, ' $1').trim();
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading cake edit logs...</Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">Error loading logs: {error}</Typography>
      </Box>
    );
  }

  if (!logs || logs.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="textSecondary">No cake edit logs found for this order.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Cake Edit History ({logs.length} changes)
      </Typography>
      
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
           <TableHead>
             <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
               <TableCell sx={{ textAlign: 'center' }}>Edited User</TableCell>
               <TableCell sx={{ textAlign: 'center' }}>Cake Details</TableCell>
               <TableCell sx={{ textAlign: 'center' }}>Price Change</TableCell>
               <TableCell sx={{ textAlign: 'center' }}>Date & Time</TableCell>
               <TableCell sx={{ textAlign: 'center' }}>Actions</TableCell>
             </TableRow>
           </TableHead>
          <TableBody>
            {logs.map((log: CakeEditLog) => (
              <TableRow key={log.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      <PersonIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {log.changes?.names?.userFullName || 'Unknown User'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {log.changes?.names?.userName || 'No email'}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Box>
                    {(() => {
                      const changedFields = [];
                      
                      // Check if shape changed
                      if (log.changes?.shapeId) {
                        changedFields.push(
                          <Typography key="shape" variant="body2" fontWeight="bold" color="primary">
                            Shape: {log.changes.names?.shapeName || 'Shape Changed'}
                          </Typography>
                        );
                      }
                      
                      // Check if flavor changed
                      if (log.changes?.flavorId) {
                        changedFields.push(
                          <Typography key="flavor" variant="body2" fontWeight="bold" color="primary">
                            Flavor: {log.changes.names?.flavorName || 'Flavor Changed'}
                          </Typography>
                        );
                      }
                      
                      // Check if topping changed
                      if (log.changes?.toppingId) {
                        changedFields.push(
                          <Typography key="topping" variant="body2" fontWeight="bold" color="primary">
                            Topping: {log.changes.names?.toppingName || 'Topping Changed'}
                          </Typography>
                        );
                      }
                      
                      // Check if color changed
                      if (log.changes?.colorCode) {
                        changedFields.push(
                          <Typography key="color" variant="body2" fontWeight="bold" color="primary">
                            Color: {log.changes.colorCode.from} → {log.changes.colorCode.to}
                          </Typography>
                        );
                      }
                      
                      // Check if write on cake changed (only show if not empty to empty)
                      if (log.changes?.writeOnCake) {
                        const fromValue = log.changes.writeOnCake.from || '(empty)';
                        const toValue = log.changes.writeOnCake.to || '(empty)';
                        // Don't show if both are empty
                        if (!(fromValue === '(empty)' && toValue === '(empty)' && !log.changes.writeOnCake.from && !log.changes.writeOnCake.to)) {
                          changedFields.push(
                            <Typography key="writeon" variant="body2" fontWeight="bold" color="primary">
                              Write on Cake: {fromValue} → {toValue}
                            </Typography>
                          );
                        }
                      }
                      
                      // Check if display mode changed
                      if (log.changes?.displayMode) {
                        changedFields.push(
                          <Typography key="display" variant="body2" fontWeight="bold" color="primary">
                            Display: {log.changes.displayMode.from} → {log.changes.displayMode.to}
                          </Typography>
                        );
                      }
                      
                      if (changedFields.length === 0) {
                        return (
                          <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                            Price only
                          </Typography>
                        );
                      }
                      
                      return changedFields;
                    })()}
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {formatPrice(log.oldPrice)} → {formatPrice(log.newPrice)}
                    </Typography>
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTimeIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {formatDateTime(log.updatedAt)}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell>
                  <IconButton 
                    onClick={() => handleViewDetails(log)}
                    color="primary"
                    size="small"
                    title="View Details"
                  >
                    <VisibilityIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detailed View Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VisibilityIcon color="primary" />
            <Typography variant="h6">Cake Edit Details</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {selectedLog && (
            <Box sx={{ mt: 2 }}>
              {/* User Information */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  User Information
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ width: 48, height: 48 }}>
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedLog.changes?.names?.userFullName || 'Unknown User'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {selectedLog.changes?.names?.userName || 'No email'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Cake Details */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Cake Details
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold" color="textSecondary">
                      Shape:
                    </Typography>
                    <Typography variant="body1">
                      {selectedLog.changes?.names?.shapeName || 'Unknown Shape'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight="bold" color="textSecondary">
                      Flavor:
                    </Typography>
                    <Typography variant="body1">
                      {selectedLog.changes?.names?.flavorName || 'Unknown Flavor'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight="bold" color="textSecondary">
                      Topping:
                    </Typography>
                    <Typography variant="body1">
                      {selectedLog.changes?.names?.toppingName || 'No Topping'}
                    </Typography>
                  </Box>
                  {selectedLog.changes?.colorCode && (
                    <Box>
                      <Typography variant="body2" fontWeight="bold" color="textSecondary">
                        Color:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <div
                          style={{
                            width: 24,
                            height: 24,
                            backgroundColor: selectedLog.changes.colorCode.to || '#ffffff',
                            borderRadius: '50%',
                            border: '1px solid #ccc',
                          }}
                        />
                        <Typography variant="body1">
                          {selectedLog.changes.colorCode.from || '(none)'} → {selectedLog.changes.colorCode.to || '(none)'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {selectedLog.changes?.writeOnCake && (() => {
                    const fromValue = selectedLog.changes.writeOnCake.from || '(empty)';
                    const toValue = selectedLog.changes.writeOnCake.to || '(empty)';
                    // Don't show if both are empty
                    if (fromValue === '(empty)' && toValue === '(empty)' && !selectedLog.changes.writeOnCake.from && !selectedLog.changes.writeOnCake.to) {
                      return null;
                    }
                    return (
                      <Box>
                        <Typography variant="body2" fontWeight="bold" color="textSecondary">
                          Write on Cake:
                        </Typography>
                        <Typography variant="body1">
                          {fromValue} → {toValue}
                        </Typography>
                      </Box>
                    );
                  })()}
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Price Information */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Price Information
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1">
                    {formatPrice(selectedLog.oldPrice)} → {formatPrice(selectedLog.newPrice)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold" color="textSecondary">
                      Old Price:
                    </Typography>
                    <Typography variant="body1">
                      {formatPrice(selectedLog.oldPrice)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight="bold" color="textSecondary">
                      New Price:
                    </Typography>
                    <Typography variant="body1">
                      {formatPrice(selectedLog.newPrice)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Field Changes */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Field Changes
                </Typography>
                {selectedLog.changes && Object.keys(selectedLog.changes).filter(key => key !== 'names').length > 0 ? (
                  <Box>
                    {Object.entries(selectedLog.changes).map(([field, change]: [string, any]) => {
                      if (field === 'names') return null; // Skip names as they're shown above
                      return (
                        <Box key={field} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="body2" fontWeight="bold" color="primary">
                              {getFieldDisplayName(field)}
                            </Typography>
                            <Chip 
                              label="CHANGED" 
                              size="small" 
                              color="warning" 
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: '20px' }}
                            />
                          </Box>
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="textSecondary">
                              <span style={{ textDecoration: 'line-through', color: '#f44336' }}>
                                {(() => {
                                  // For IDs, try to get meaningful names instead of showing raw IDs
                                  if (field === 'shapeId' && selectedLog.changes?.names?.shapeName) {
                                    const parts = selectedLog.changes.names.shapeName.split(' → ');
                                    return parts[0] || change.from;
                                  }
                                  if (field === 'flavorId' && selectedLog.changes?.names?.flavorName) {
                                    const parts = selectedLog.changes.names.flavorName.split(' → ');
                                    return parts[0] || change.from;
                                  }
                                  if (field === 'toppingId' && selectedLog.changes?.names?.toppingName) {
                                    const parts = selectedLog.changes.names.toppingName.split(' → ');
                                    return parts[0] || change.from;
                                  }
                                  if (field === 'colorCode') {
                                    return change.from || '(none)';
                                  }
                                  if (field === 'writeOnCake') {
                                    return change.from || '(empty)';
                                  }
                                  return change.from || '(none)';
                                })()}
                              </span>
                              {' → '}
                              <span style={{ color: '#4caf50', fontWeight: 'bold' }}>
                                {(() => {
                                  // For IDs, try to get meaningful names instead of showing raw IDs
                                  if (field === 'shapeId' && selectedLog.changes?.names?.shapeName) {
                                    const parts = selectedLog.changes.names.shapeName.split(' → ');
                                    return parts[1] || change.to;
                                  }
                                  if (field === 'flavorId' && selectedLog.changes?.names?.flavorName) {
                                    const parts = selectedLog.changes.names.flavorName.split(' → ');
                                    return parts[1] || change.to;
                                  }
                                  if (field === 'toppingId' && selectedLog.changes?.names?.toppingName) {
                                    const parts = selectedLog.changes.names.toppingName.split(' → ');
                                    return parts[1] || change.to;
                                  }
                                  if (field === 'colorCode') {
                                    return change.to || '(none)';
                                  }
                                  if (field === 'writeOnCake') {
                                    return change.to || '(empty)';
                                  }
                                  return change.to || '(none)';
                                })()}
                              </span>
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
                    <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                      No field changes detected - Only price was updated
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Date & Time */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Date & Time
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AccessTimeIcon color="action" />
                  <Typography variant="body1">
                    {formatDateTime(selectedLog.updatedAt)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CakeEditLogs;
